/**
 * enums
 */
import { BadgeAppearance } from "../../enums";
import { ThemeColorsVariants } from "@core/enums/theme-colors-variants";

/**
 * interfaces
 */
import { IBadge } from "../../interfaces";


/**
 * BadgeModel class is a model for the badge directive.
 * It is used to create a badge model.
 * @param _value - The value of the badge model
 * @param _label - The label of the badge model
 * @param _variant - The variant of the badge model
 * @example
 * ```typescript
 * const badgeModel = new BadgeModel<string>(true, 'Active', ThemeColorsVariants.Success);
 * ```
 * @returns {BadgeModel} - The badge model
 */
export class BadgeModel<TValue = unknown> implements IBadge<TValue> {

    /**
     * The value of the badge model
     */
    public get value(): TValue {
        return this._value;
    }

    /**
     * The label of the badge model
     */
    public get label(): string {
        return this._label;
    }

    /**
     * The variant of the badge model
     */
    public get variant(): ThemeColorsVariants {
        return this._variant;
    }

    /**
     * The appearance of the badge model
     */
    public get appearance(): BadgeAppearance | undefined {
        return this._appearance;
    }

    /**
     * The glow of the badge model
     */
    public glow?: boolean;

    /**
     * The rounded of the badge model
     */
    public rounded?: boolean;

    /**
     * The center of the badge model
     */
    public center?: boolean;

    /**
     * The notifications of the badge model
     */
    public notifications?: boolean;

    /**
     * The dot of the badge model
     */
    public dot?: boolean;

    /**
     * The class of the badge model
     */
    public class?: string;

    /**
     * @param value - The value of the badge model
     * @param label - The label of the badge model
     * @param variant - The variant of the badge model
     */
    constructor(
        private _value: TValue,
        private _label: string,
        private _variant: ThemeColorsVariants,
        private _appearance?: BadgeAppearance,
    ) {}

    /**
     * Sets the value of the badge model
     */
    public setValue(value: TValue) {
        this._value = value;
    }

    /**
     * Sets the label of the badge model
     */
    public setLabel(label: string) {
        this._label = label;
    }
    
    /**
     * Sets the variant of the badge model
     */
    public setVariant(variant: ThemeColorsVariants) {
        this._variant = variant;
    }

    /**
     * Sets the appearance of the badge model
     */
    public setAppearance(appearance: BadgeAppearance) {
        this._appearance = appearance;
    }
}
